import omit from "lodash/omit";
import {
  CircleArrowDown,
  Disc3,
  Heart,
  Info,
  ListEnd,
  ListPlus,
  ListX,
  Pencil,
  PlayIcon,
  PlusIcon,
  Trash,
  Trash2,
  User,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { ContextMenuItem } from "@/app/components/ui/context-menu";
import { DropdownMenuItem } from "@/app/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { MenuItemFactory } from "./menu-item-factory";
import { SubMenuFactory } from "./sub-menu-factory";

type DropdownMenuItemProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuItem | typeof ContextMenuItem
> & {
  variant?: "dropdown" | "context";
};

function Play({ variant = "dropdown", ...props }: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<PlayIcon className="mr-2 h-4 w-4 fill-foreground" />}
      label={t("options.play")}
      {...props}
    />
  );
}

function PlayNext({ variant = "dropdown", ...props }: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<ListPlus className="mr-2 h-4 w-4" />}
      label={t("options.playNext")}
      {...props}
    />
  );
}

function PlayLast({ variant = "dropdown", ...props }: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<ListEnd className="mr-2 h-4 w-4" />}
      label={t("options.addLast")}
      {...props}
    />
  );
}

function EditPlaylist({
  variant = "dropdown",
  ...props
}: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<Pencil className="mr-2 h-4 w-4" />}
      label={t("options.playlist.edit")}
      {...props}
    />
  );
}

function RemovePlaylist({
  variant = "dropdown",
  ...props
}: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<Trash className="mr-2 h-4 w-4 fill-red-300 text-red-500" />}
      label={t("options.playlist.delete")}
      {...props}
    />
  );
}

export function AddToPlaylistOption({
  variant = "dropdown",
  children,
  ...props
}: DropdownMenuItemProps) {
  const { t } = useTranslation();
  const propsWithoutChildren = omit(props, "children");

  return (
    <SubMenuFactory
      variant={variant}
      icon={<PlusIcon className="mr-2 h-4 w-4" />}
      label={t("options.playlist.add")}
      {...propsWithoutChildren}
    >
      {children}
    </SubMenuFactory>
  );
}

function RemoveFromPlaylist({
  variant = "dropdown",
  ...props
}: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<Trash className="mr-2 h-4 w-4 fill-red-300 text-red-500" />}
      label={t("options.playlist.removeSong")}
      {...props}
    />
  );
}

function RemoveFromQueue({
  variant = "dropdown",
  ...props
}: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<ListX className="mr-2 h-4 w-4" />}
      label={t("options.removeFromQueue")}
      {...props}
    />
  );
}

function SongInfo({ variant = "dropdown", ...props }: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<Info className="mr-2 h-4 w-4" />}
      label={t("options.info")}
      {...props}
    />
  );
}

function GotoArtist({ variant = "dropdown", ...props }: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<User className="mr-2 h-4 w-4" />}
      label={t("options.goto.artist")}
      {...props}
    />
  );
}

function GotoAlbum({ variant = "dropdown", ...props }: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<Disc3 className="mr-2 h-4 w-4" />}
      label={t("options.goto.album")}
      {...props}
    />
  );
}

type LikeProps = DropdownMenuItemProps & {
  isStarred?: boolean;
  label?: string;
};

function Like({ variant = "dropdown", isStarred, label, ...props }: LikeProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={
        <Heart
          className={cn(
            "mr-2 h-4 w-4",
            isStarred && "text-red-500 fill-red-500",
          )}
        />
      }
      label={label ?? (isStarred ? t("options.unlike") : t("options.like"))}
      {...props}
    />
  );
}

function DownloadSong({
  variant = "dropdown",
  ...props
}: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<CircleArrowDown className="mr-2 h-4 w-4" />}
      label={t("options.download")}
      {...props}
    />
  );
}

function DownloadAlbum({
  variant = "dropdown",
  ...props
}: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<CircleArrowDown className="mr-2 h-4 w-4" />}
      label={t("options.downloadAlbum")}
      {...props}
    />
  );
}

function DownloadPlaylist({
  variant = "dropdown",
  ...props
}: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<CircleArrowDown className="mr-2 h-4 w-4" />}
      label={t("options.downloadPlaylist")}
      {...props}
    />
  );
}

function DownloadArtist({
  variant = "dropdown",
  ...props
}: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<CircleArrowDown className="mr-2 h-4 w-4" />}
      label={t("options.downloadArtist")}
      {...props}
    />
  );
}

function RemoveDownload({
  variant = "dropdown",
  ...props
}: DropdownMenuItemProps) {
  const { t } = useTranslation();

  return (
    <MenuItemFactory
      variant={variant}
      icon={<Trash2 className="mr-2 h-4 w-4" />}
      label={t("options.removeDownload")}
      {...props}
    />
  );
}

export const OptionsButtons = {
  Play,
  PlayNext,
  PlayLast,
  DownloadSong,
  Like,
  AddToPlaylistOption,
  EditPlaylist,
  RemovePlaylist,
  RemoveFromPlaylist,
  RemoveFromQueue,
  SongInfo,
  GotoArtist,
  GotoAlbum,
  DownloadAlbum,
  DownloadPlaylist,
  DownloadArtist,
  RemoveDownload,
};
